<template>
    <vx-expandable-card>
        <template v-slot:header> To </template>

        <template v-slot:headerSubtitle> Contacts that should receive this campaign. Segments are limited to the
            matching
            audience type selected for this campaign. </template>

        <template v-slot:content>
            <vx-tabs
                v-if="canChooseCampaign || props.campaignDurationType === 'one_time' || FEATURES.external_contact_lists"
                :disabled="props.disabled"
                data-test="audience-tabs"
                v-model="currentTab"
                size="lg">
                <vx-tab v-if="canChooseCampaign"
                    value="campaign">Campaign</vx-tab>
                <vx-tab value="segments">Segments</vx-tab>
                <vx-tab value="external-contact-lists" v-if="FEATURES.external_contact_lists">External List</vx-tab>
                <vx-tab v-if="props.campaignDurationType === 'one_time'"
                    value="everyone">Everyone</vx-tab>
            </vx-tabs>

            <div v-if="currentTab === 'campaign' && canChooseCampaign"
                class="mt-4 max-w-full">
                <campaigns-selectable size="lg"
                    :modelValue="props.originalCampaignId"
                    @update:modelValue="emit('update:originalCampaignId', $event)"
                    :disabled="props.disabled"
                    :flowId="props.flowId"
                    clearable
                    :ids="props.originalCampaignId ? [props.originalCampaignId] : []">
                </campaigns-selectable>
            </div>

            <div v-if="currentTab === 'segments'"
                class="mt-4 max-w-full">
                <segments-selectable size="lg"
                    :error="displayFirstError(validate.segmentIds)"
                    :modelValue="props.segmentIds"
                    :disabled="props.disabled"
                    :flowId="props.flowId"
                    :audienceType="props.campaignType"
                    @update:modelValue="emit('update:segmentIds', $event)">
                </segments-selectable>
            </div>

            <div v-if="currentTab === 'external-contact-lists'"
                class="mt-4 max-w-full">
                <external-contact-lists-selectable size="lg"
                    :error="displayFirstError(validate.externalContactListIds)"
                    :audience-type="props.campaignType"
                    :modelValue="props.externalContactListIds"
                    :disabled="props.disabled"
                    :flowId="props.flowId"
                    @update:modelValue="emit('update:externalContactListIds', $event)">
                </external-contact-lists-selectable>
            </div>

            <div class="mt-8 text-slate-700 flex flex-col gap-2 text-sm">
                <div class="text-lg font-extrabold mb-4">Contact Settings</div>
                <div class="p-6 bg-white items-center rounded-2xl justify-between flex">
                    <div>
                        <strong>Remove</strong> contacts if they respond?
                    </div>
                    <div>
                        <vx-toggle size="sm" data-test="remove-on-response-toggle"
                            :modelValue="props.removeOnResponse"
                            @update:modelValue="emit('update:removeOnResponse', $event)"></vx-toggle>
                    </div>
                </div>
                <div class="p-6 bg-white items-center rounded-2xl justify-between flex">
                    <div>
                        <strong>Remove</strong> contacts if they follow a link?
                    </div>
                    <div>
                        <vx-toggle size="sm" data-test="remove-on-link-click-toggle"
                            :modelValue="props.removeOnLinkClick"
                            @update:modelValue="emit('update:removeOnLinkClick', $event)"></vx-toggle>
                    </div>
                </div>
                <div v-if="props.campaignDurationType !== 'one_time'"
                    class="p-6 bg-white items-center rounded-2xl">
                    <div class="justify-between flex">
                        <div>
                            Send to contacts <strong>more than once</strong>?
                        </div>
                        <div>
                            <vx-toggle size="sm" :disabled="props.disabled"
                                data-test="is-repeatable-toggle"
                                :modelValue="props.isRepeatable"
                                @update:modelValue="emit('update:isRepeatable', $event)"></vx-toggle>
                        </div>
                    </div>
                    <div v-if="props.isRepeatable">
                        <div class="text-slate-500 text-sm mb-3">
                            How long before contacts can re-enter this campaign?
                        </div>
                        <div class="flex flex-col gap-4 lg:flex-row lg:justify-between">
                            <div class="grow">
                                <vx-selectable size="lg"
                                    :disabled="props.disabled"
                                    data-test="repeat-delay-value-selectable"
                                    :options="repeatDelayConfig.options"
                                    v-model="repeatDelayValue"
                                    @update:modelValue="emitRepeatDelay()"
                                    :getOptionLabel="option => option"
                                    :reduce="option => option"
                                    :clearable="false">
                                </vx-selectable>
                            </div>
                            <vx-tabs data-test="repeat-delay-unit-tabs"
                                class="lg:shrink-0"
                                :disabled="props.disabled"
                                size="lg"
                                v-model="repeatDelayUnit">
                                <vx-tab value="minutes">Minutes</vx-tab>
                                <vx-tab value="hours">Hours</vx-tab>
                                <vx-tab value="days">Days</vx-tab>
                                <vx-tab value="weeks">Weeks</vx-tab>
                            </vx-tabs>
                        </div>
                    </div>

                </div>

                <div v-if="FEATURES.campaign_filtering"
                    class="p-6 bg-white items-center rounded-2xl justify-between flex flex-col lg:flex-row">
                    <div>
                        <strong>Audience Percentage</strong>
                        <div class="text-slate-500">
                            What percentage of the audience contacts that will receive messages from this campaign?
                        </div>
                    </div>
                    <div>
                        <vx-input class="lg:w-24"
                            size="lg"
                            type="number"
                            :step="0.01"
                            :min="0"
                            :max="100"
                            data-test="audience-percentage"
                            v-model="audiencePercentageModel"></vx-input>
                    </div>
                </div>
            </div>

            <div v-if="FEATURES.hybrid_message_routing" class="mt-8 text-slate-700 flex flex-col gap-2 text-sm">
                <div class="text-lg font-extrabold mb-4">Number Settings</div>
                <div class="p-6 bg-white items-center rounded-2xl justify-between flex">
                    <div>
                        Prefer using corporate numbers over group numbers.
                    </div>
                    <div>
                        <vx-toggle size="sm"
                            :disabled="props.disabled"
                            data-test="prefer-corporate-number-toggle"
                            :modelValue="preferCorporateNumberModel"
                            @update:modelValue="preferCorporateNumberModel = $event"></vx-toggle>
                    </div>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import { VxExpandableCard, VxTab, VxTabs, VxToggle, VxSelectable, VxInput } from '@voxie/frontend-components';
import { onMounted, ref, watch, computed, nextTick, inject } from 'vue';
import {
    AUDIENCE_TYPE_ACQUISITION,
    AUDIENCE_TYPE_MARKETING,
    AUDIENCE_TYPE_RE_ACQUISITION,
    AUDIENCE_TYPE_TRANSACTIONAL, MAX_EXTERNAL_CONTACT_LISTS_PER_CAMPAIGN,
    MAX_SEGMENTS_PER_CAMPAIGN,
} from '~/constants/campaign';
import SegmentsSelectable from '~/components/general/segments/SegmentsSelectable.vue';
import CampaignsSelectable from '~/components/general/campaigns/CampaignsSelectable.vue';
import useVuelidate from '@vuelidate/core';
import { helpers, maxLength } from '@vuelidate/validators';
import { displayFirstError } from '~/utils/validation';
import ExternalContactListsSelectable
    from "~/components/general/external-contact-lists/ExternalContactListsSelectable.vue";

const FEATURES = inject('FEATURES');

const props = defineProps({
    campaignId: {
        type: Number,
        default: undefined,
    },
    campaignType: {
        type: String,
        default: undefined,
        validator: (campaignType) => [AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL, AUDIENCE_TYPE_ACQUISITION, AUDIENCE_TYPE_RE_ACQUISITION].includes(campaignType)
    },
    campaignDurationType: {
        type: String,
    },
    campaignStatus: {
        type: String,
    },
    segmentIds: {
        type: Array,
        default: () => ([]),
    },
    externalContactListIds: {
        type: Array,
        default: () => ([]),
    },
    originalCampaignId: {
        type: Number,
        default: undefined,
    },
    everyone: {
        type: Boolean,
        default: undefined,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    removeOnResponse: {
        type: Boolean,
        default: false,
    },
    removeOnLinkClick: {
        type: Boolean,
        default: false,
    },
    isRepeatable: {
        type: Boolean,
        default: false,
    },
    repeatDelay: {
        type: Number,
        default: 10080,
    },
    audiencePercentage: {
        type: Number,
        default: 0,
    },
    preferCorporateNumber: {
        type: Boolean,
        default: true,
    },
    flowId: {
        type: [String, undefined],
        default: undefined,
    },
});

const currentTab = ref('segments');
const mounted = ref(false);

const emit = defineEmits([
    "update:segmentIds",
    "update:externalContactListIds",
    "update:originalCampaignId",
    "update:everyone",
    "update:removeOnResponse",
    "update:removeOnLinkClick",
    "update:isRepeatable",
    "update:repeatDelay",
    "update:audiencePercentage",
    "update:preferCorporateNumber"
]);

const canChooseCampaign = ref(false);

const audiencePercentageModel = computed({
    get: () => {
        if (props.audiencePercentage === null || props.audiencePercentage === undefined) {
            return null;
        }

        return String(parseFloat((props.audiencePercentage * 100).toFixed(2)));
    },

    // Setter converts the entered percentage back to a decimal for the backend
    set: (value) => {
        if (isNaN(parseFloat(value))) {
            emit('update:audiencePercentage', null);
        } else {
            if (value > 100) {
                value = 100;
            }

            if (value < 0) {
                value = 0;
            }

            const decimalValue = parseFloat(value) / 100;
            // Round to 4 decimal places
            emit('update:audiencePercentage', parseFloat(decimalValue.toFixed(4)))
        }
    }
});

const preferCorporateNumberModel = computed({
    get: () => {
        // Backend can return null, true, or false
        // We interpret null or true as true (checked)
        return props.preferCorporateNumber !== false;
    },
    set: (value) => {
        emit('update:preferCorporateNumber', value);
    }
});

const validate = useVuelidate({
    segmentIds: {
        maxLength: helpers.withMessage(`A campaign can only contain up to ${MAX_SEGMENTS_PER_CAMPAIGN} segments.`, maxLength(MAX_SEGMENTS_PER_CAMPAIGN)),
    },
    externalContactListIds: {
        maxLength: helpers.withMessage(`A campaign can only contain up to ${MAX_EXTERNAL_CONTACT_LISTS_PER_CAMPAIGN} external lists.`, maxLength(MAX_EXTERNAL_CONTACT_LISTS_PER_CAMPAIGN)),
    },
}, computed(() => ({
    segmentIds: props.segmentIds,
    externalContactListIds: props.externalContactListIds,
})))

onMounted(async () => {
    if (props.originalCampaignId) {
        currentTab.value = 'campaign';
        canChooseCampaign.value = true;
    } else if (props.segmentIds?.length) {
        currentTab.value = 'segments';
    } else if (props.externalContactListIds?.length) {
        currentTab.value = 'external-contact-lists';
    } else if (props.everyone) {
        currentTab.value = 'everyone';
    }

    processRepeatDelayInMinutes();

    await nextTick();
    mounted.value = true;
});

watch(currentTab, () => {
    if (!mounted.value) {
        return;
    }

    if (currentTab.value === 'campaign') {
        emit('update:everyone', false);
        emit('update:segmentIds', [])
        emit('update:externalContactListIds', [])
    } else if (currentTab.value === 'segments') {
        emit('update:everyone', false);
        emit('update:externalContactListIds', []);
        emit('update:originalCampaignId', null);
    } else if (currentTab.value === 'external-contact-lists') {
        emit('update:everyone', false);
        emit('update:segmentIds', []);
        emit('update:originalCampaignId', null);
    } else if (currentTab.value === 'everyone') {
        emit('update:everyone', true);
        emit('update:segmentIds', []);
        emit('update:externalContactListIds', []);
        emit('update:originalCampaignId', null);
    }
});

const repeatDelayUnit = ref('minutes');
const getRepeatDelayConfig = (unit) => {
    if (unit === 'hours') {
        return {
            multiplier: 60,
            options: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23],
        };
    } else if (unit === 'days') {
        return {
            multiplier: 60 * 24,
            options: [1, 2, 3, 4, 5, 6]
        };
    } else if (unit === 'weeks') {
        return {
            multiplier: 60 * 24 * 7,
            options: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
        }
    } else {
        return {
            multiplier: 1,
            options: [15, 30, 45],
        };
    }
}
const repeatDelayConfig = computed(() => getRepeatDelayConfig(repeatDelayUnit.value));
const repeatDelayValue = ref(repeatDelayConfig.value.options[0]);

const emitRepeatDelay = () => {
    emit('update:repeatDelay', Number(repeatDelayValue.value) * Number(repeatDelayConfig.value.multiplier));
}

const processRepeatDelayInMinutes = () => {
    const repeatDelay = Number(props.repeatDelay);
    if (repeatDelay < 60) {
        repeatDelayUnit.value = 'minutes';
        repeatDelayValue.value = repeatDelay;
        return;
    }
    for (const unit of ['weeks', 'days', 'hours']) {
        const config = getRepeatDelayConfig(unit);

        const multiplier = config.multiplier;
        if (repeatDelay % multiplier === 0) {
            repeatDelayUnit.value = unit;
            repeatDelayValue.value = repeatDelay / multiplier;
            return;
        }
    }

    repeatDelayUnit.value = 'minutes';
    repeatDelayValue.value = repeatDelay;
}

watch(repeatDelayUnit, async () => {
    if (!mounted.value) {
        return;
    }

    repeatDelayValue.value = repeatDelayConfig.value.options[0];
    emitRepeatDelay()
});

</script>
